import { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  CountryCodeSelect,
  Country,
} from "@/components/countries-dropdown/CountriesDropdown";
import { useTranslation } from "react-i18next";
import { useTransition } from "react-transition-state";
import { UserContext } from "@/contexts/continente-credentials/UserContext";

import { isNumber } from "@/utils/utils";
import Input from "../ui/Input";
import SimpleErrorMessage from "../error-message/new-error-message";

type InputType = "phone" | "email-phone" | "email";
type AutocompleteWith = "primaryContact" | "phone" | "email";
export type UsernameInputChangeSource = "user" | "prefill" | "country";

type UsernameInputProps = {
  id?: string;
  type: InputType;
  value: string;
  disabled?: boolean;
  ariaLabel?: string;
  ariaReadonly?: boolean;
  ariaDisabled?: boolean;
  ariaHidden?: boolean;
  isError?: boolean;
  errorMsg?: string;
  isFilled?: boolean;
  defaultCountryCode?: string;
  defaultPhoneCountryCode?: string;
  defaultValue?: string;
  placeholder?: string;
  autocompleteWith?: AutocompleteWith;
  autoComplete?: string;
  required?: boolean;
  onInvalid?: (e: any) => void;
  onChange: (
    input: string,
    phoneNumberCode: string,
    source?: UsernameInputChangeSource
  ) => void;
};

function UsernameInput({
  id = "userNameCC",
  type,
  value = "",
  disabled,
  ariaLabel,
  ariaReadonly,
  ariaDisabled,
  ariaHidden,
  isError,
  errorMsg,
  isFilled,
  defaultCountryCode = "PT",
  defaultPhoneCountryCode = "+351",
  defaultValue = "",
  autocompleteWith = "primaryContact" as AutocompleteWith,
  autoComplete = "username",
  required = false,
  onInvalid,
  onChange = (
    input: string,
    countryCode: string,
    _source?: UsernameInputChangeSource
  ): void => void 0,
  ...props
}: UsernameInputProps) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState(false);
  const [hover, setHover] = useState(false);
  const [phoneNumberCountryCode, setPhoneNumberCountryCode] = useState(
    defaultPhoneCountryCode
  );
  const [inputValue, setInputValue] = useState(value);
  const [countriesDropdownTransition, setCountriesDropdownTransition] =
    useTransition({
      timeout: 200,
      preEnter: true,
    });

  const userContext = useContext(UserContext);

  const transitionClass = `transition ${countriesDropdownTransition}`;

  let label: string;
  switch (type) {
    case "phone":
      label = t("common.phonenumber");
      break;
    case "email-phone":
      label = t("common.email_and_phone");
      break;
    case "email":
      label = t("common.email");
      break;
  }

  const handleChange = (
    input: string,
    phoneNumberCountryCode: string,
    source: UsernameInputChangeSource = "user"
  ) => {
    input = input?.replace(/\s+/g, "");
    onChange(input, phoneNumberCountryCode, source);
  };

  useEffect(() => {
    if (type === "email-phone") {
      const isCurrentInputValueNumber = isNumber(inputValue);
      if (!isCurrentInputValueNumber) {
        setCountriesDropdownTransition(false);
      }
      if (isCurrentInputValueNumber) {
        setCountriesDropdownTransition(true);
      }
    }
    else if (type === "phone") {
      setCountriesDropdownTransition(true);
    }
    else {
      setCountriesDropdownTransition(false);
    }
  }, [value])

  useEffect(() => {
    if (isError) {
      setCountriesDropdownTransition(false);
      document.getElementById("userNameCC__error-message")?.focus();
    }
  }, [isError]);

  // Added because of the CC-2095
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // Removed because of CC-2035 
  // useEffect(() => {
  //   if (type === "email-phone") {
  //     if (userContext.user.primaryContact === "phoneNumber") {
  //       setInputValue(defaultValue ?? userContext.user.phoneNumber);
  //       setPhoneNumberCountryCode(
  //         userContext.user.phoneNumberCode !== ""
  //           ? userContext.user.phoneNumberCode
  //           : phoneNumberCountryCode
  //       );
  //       handleChange(
  //         defaultValue ?? userContext.user.phoneNumber,
  //         phoneNumberCountryCode,
  //         "prefill"
  //       );
  //     } else {
  //       setInputValue(userContext.user.email);
  //     }
  //   }
  //   if (type === "phone") {
  //     setInputValue(
  //       userContext.user.phoneNumber !== ""
  //         ? userContext.user.phoneNumber
  //         : defaultValue !== "" ? defaultValue : value
  //     );
  //     setPhoneNumberCountryCode(
  //       userContext.user.phoneNumberCode !== ""
  //         ? userContext.user.phoneNumberCode
  //         : phoneNumberCountryCode
  //     );
  //     handleChange(
  //       defaultValue ?? userContext.user.phoneNumber,
  //       phoneNumberCountryCode,
  //       "prefill"
  //     );
  //   } else {
  //     setInputValue(userContext.user.email);
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const onInputChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>): void => {
    value = value.replace(/\s+/g, "");

    setInputValue(value);

    if (type === "email-phone") {
      const isCurrentInputValueNumber = isNumber(inputValue);
      const isNewInputValueNumber = isNumber(value);

      if (!isCurrentInputValueNumber && isNewInputValueNumber) {
        setCountriesDropdownTransition(true);
      }

      if (isCurrentInputValueNumber && !isNewInputValueNumber) {
        setCountriesDropdownTransition(false);
      }
    }
    else if (type === "phone") {
      setCountriesDropdownTransition(true);
    }
    else {
      setCountriesDropdownTransition(false);
    }

    handleChange(value, phoneNumberCountryCode, "user");
  };

  const onCountriesDropdownChange = (country: Country) => {
    userContext.updateUser({
      ...userContext.user,
      countryCode: country.code,
    });
    setPhoneNumberCountryCode(country.phoneNumberCode);
    handleChange(inputValue, country.phoneNumberCode, "country");
  };

  const onFocusChange = (value: boolean): void => {
    setFocus(value);
  };

  const onHoverChange = (value: boolean): void => {
    setHover(value);
  };

  const category = (type === "phone" || type === "email-phone") ? "phone" : "text";
  const inputMode = type === "phone" ? "numeric" : type === "email" ? "email" : "text";

  const showDropdown =
    type === "email" ? false :
      type === "phone" ? true :
        type === "email-phone" && isNumber(inputValue);

  return (
    <div className="w-full">
      <div className={`flex username-input ${transitionClass} w-full`}>
        {showDropdown &&
          <div aria-hidden className="transition-[width] duration-200 ease-in group-[.transition]:w-16 group-[.transition.entering]:w-16 group-[.transition.entered]:w-16 flex self-end">
            <CountryCodeSelect
              onChange={onCountriesDropdownChange}
              isError={isError}
              isFocus={focus && !disabled}
              isHover={hover && !disabled}
              disabled={disabled}
              defaultCountryCode={defaultCountryCode}
              defaultPhoneNumberCode={defaultPhoneCountryCode}
              value={phoneNumberCountryCode}
            />
          </div>
        }
        <Input
          id={id}
          name={id}
          labelText={label}
          isError={isError}
          errorMsg={errorMsg}
          isFilled={isFilled}
          disabled={disabled}
          onChange={onInputChange}
          placeholder={props.placeholder}
          value={inputValue}
          onFocus={onFocusChange}
          onHover={onHoverChange}
          ariaInvalid={isError}
          ariaLabel={ariaLabel || label}
          ariaReadonly={ariaReadonly}
          ariaHidden={ariaHidden}
          ariaDisabled={ariaDisabled}
          autoComplete={autoComplete}
          ariaDescribedBy={`${id}__error-message`}
          ariaErrorMessage={`${id}__error-message`}
          required={required}
          onInvalid={onInvalid}
          showError={false}
          category={category}
          inputMode={inputMode}
          showDropdown={showDropdown}
        />
      </div>

      <SimpleErrorMessage
        id={id + "__error-message"}
        error={errorMsg}
      />
    </div>
  );
}

export default UsernameInput;
